import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { Mail, Phone, Building2, RefreshCw } from 'lucide-react';

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-amber-100 text-amber-800',
  scheduled: 'bg-indigo-100 text-indigo-800',
  won: 'bg-emerald-100 text-emerald-800',
  lost: 'bg-gray-100 text-gray-600',
};

const DemoRequestsView = () => {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async (status = filter) => {
    setLoading(true);
    try {
      const res = await api.get('/api/demo-requests', { params: { status } });
      setRequests(res.data.requests || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      console.error('Failed to load demo requests', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); }, [filter]);

  const updateStatus = async (requestId, status) => {
    try {
      await api.patch(`/api/demo-requests/${requestId}`, { status });
      load(filter);
    } catch (e) {
      console.error('Failed to update demo request', e);
    }
  };

  return (
    <div className="space-y-6" data-testid="demo-requests-view">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Demo Requests</h3>
            <p className="text-sm text-gray-500 mt-1">Enterprise buyers who asked for a walkthrough from the website</p>
          </div>
          <button onClick={() => load(filter)} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900" data-testid="demo-requests-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {[{ value: 'all', label: 'All' }, ...STATUSES].map(s => (
            <button
              key={s.value}
              onClick={() => setFilter(s.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === s.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`demo-filter-${s.value}`}
            >
              {s.label}{s.value !== 'all' && counts[s.value] ? ` (${counts[s.value]})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500">Loading demo requests…</div>
        ) : requests.length === 0 ? (
          <div className="py-16 text-center text-gray-500" data-testid="demo-requests-empty">No demo requests yet.</div>
        ) : (
          <div className="space-y-4">
            {requests.map(r => (
              <div key={r.request_id} className="border border-gray-200 rounded-xl p-5 hover:border-blue-400 transition-colors" data-testid={`demo-request-${r.request_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h4 className="font-bold text-gray-900">{r.name}</h4>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}>
                        {r.status}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600">
                      <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-gray-400" />{r.company}{r.company_size ? ` · ${r.company_size} employees` : ''}</span>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 text-blue-600 hover:underline"><Mail className="w-4 h-4" />{r.email}</a>
                      {r.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-gray-400" />{r.phone}</span>}
                    </div>
                    {r.message && <p className="mt-3 text-sm text-gray-700 bg-gray-50 border-l-2 border-blue-500 pl-3 py-2 rounded-r">{r.message}</p>}
                    <p className="mt-2 text-xs text-gray-400">
                      {new Date(r.created_at).toLocaleString()}
                      {r.alert_email_sent ? (
                        <span className="ml-3 inline-flex items-center gap-1 text-emerald-600 font-medium" data-testid={`demo-alert-sent-${r.request_id}`}>
                          <Mail className="w-3 h-3" /> Alert emailed to {r.alert_email_to || 'inbox'}
                        </span>
                      ) : (
                        <span className="ml-3 inline-flex items-center gap-1 text-amber-600 font-medium" data-testid={`demo-alert-failed-${r.request_id}`}>
                          <Mail className="w-3 h-3" /> Email alert not sent
                        </span>
                      )}
                    </p>
                  </div>
                  <select
                    value={r.status}
                    onChange={(e) => updateStatus(r.request_id, e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    data-testid={`demo-status-select-${r.request_id}`}
                  >
                    {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoRequestsView;
